/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo } from 'react';
import useScales from 'hook/scales';
import { useAgenda } from 'hook/agenda';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { Avatar } from '@chakra-ui/react';

export default function DoctorsSchedule() {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [agendasToday, setAgendasToday] = useState([]);
  const [carouselIndex, setCarouselIndex] = useState(0);

  const [filterScale, setFilterScale] = useState({
    scalePage: 1,
    scalePerPage: 30,
    scaleSearch: '',
  });

  const { getFilterScales, scalesFilter, loading: loadingScales } = useScales();
  const { data: doctorsAgendas, getAgendaMultiScale } = useAgenda();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');
  const unitId = searchParams.get('unitId');

  const formatDateTime = (date) => {
    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    };
    return date.toLocaleString('pt-BR', options).replace(',', '');
  };

  // Function to filter agendas for the current day
  const filterAgendasForToday = () => {
    const today = moment().startOf('day');
    const filteredAgendas = [];

    // 'doctorsAgendas' now contains the raw agenda items, not grouped by user
    doctorsAgendas.forEach((agenda) => {
      const agendaDate = moment(agenda.date_start).startOf('day');
      if (agendaDate.isSame(today, 'day')) {
        filteredAgendas.push(agenda);
      }
    });
    setAgendasToday(filteredAgendas);
  };

  // Real-time clock
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch scales
  useEffect(() => {
    if (unitId) {
      getFilterScales(
        filterScale.scalePage,
        filterScale.scalePerPage,
        unitId,
        token,
        moment().format('YYYY-MM-DD'),
        moment().format('YYYY-MM-DD'),
        filterScale.scaleSearch,
        null
      );
    }
  }, [unitId, filterScale.scalePage, filterScale.scaleSearch]);

  // Fetch agenda and update every 30 minutes
  useEffect(() => {
    if (unitId && scalesFilter?.dataScalesFilter.length > 0) {
      const scaleIds = scalesFilter.dataScalesFilter.map((scale) => ({
        value: scale.id,
      }));
      const fetchAgenda = () => {
        getAgendaMultiScale({
          date_start: moment().format('YYYY-MM-DD'),
          date_end: moment().format('YYYY-MM-DD'),
          unit_id: unitId,
          scales: scaleIds,
          page: 1,
          per_page: 30,
          token,
        });
      };
      fetchAgenda();
      const interval = setInterval(fetchAgenda, 30 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [unitId, scalesFilter]);

  // Filter agendas for today whenever 'doctorsAgendas' or filters change
  useEffect(() => {
    if (unitId && scalesFilter?.dataScalesFilter.length > 0 && doctorsAgendas) {
      filterAgendasForToday();
    }
  }, [doctorsAgendas, unitId, scalesFilter]);

  // Group agendas by scale for rendering
  const groupedAgendasByScale = useMemo(() => {
    const grouped = {};
    agendasToday.forEach((agenda) => {
      const scale = scalesFilter?.dataScalesFilter.find(
        (s) => s.id === agenda.scale_id
      );
      if (scale) {
        if (!grouped[scale.id]) {
          grouped[scale.id] = {
            name: scale.name,
            agendas: [],
          };
        }
        grouped[scale.id].agendas.push(agenda);
      }
    });
    return Object.values(grouped); // Return an array of grouped scales
  }, [agendasToday, scalesFilter]);

  // Carousel functionality
  useEffect(() => {
    if (groupedAgendasByScale.length > 5) {
      // Assuming 5 cards per view
      const autoSlide = setInterval(() => {
        setCarouselIndex((prev) => {
          const nextIndex = prev + 5;
          return nextIndex + 5 > groupedAgendasByScale.length ? 0 : nextIndex;
        });
      }, 8000);
      return () => clearInterval(autoSlide);
    }
  }, [groupedAgendasByScale]);

  const renderScalesWithDoctors = () => {
    if (!doctorsAgendas || doctorsAgendas.length === 0) {
      return (
        <p className="text-sm text-gray-500 col-span-full text-center">
          Nenhuma escala com médicos agendados para hoje.
        </p>
      );
    }
    const escalas = doctorsAgendas;
    if (!Array.isArray(escalas)) {
      return (
        <p className="text-sm text-gray-500 col-span-full text-center">
          Dados de escala inválidos.
        </p>
      );
    }
    return escalas.map((scaleGroup) => {
      return (
        <div
          key={scaleGroup.id}
          className="min-w-[20%] p-4 bg-white rounded-lg shadow-md mx-2 max-h-full"
        >
          <h3 className="text-xl font-bold mb-3 text-blue-700">
            {scaleGroup.name}
          </h3>
          {Array.isArray(scaleGroup.agendas) &&
          scaleGroup.agendas.length > 0 ? (
            <ul
              className="text-sm text-gray-600 max-h-60 overflow-y-auto"
              style={{ paddingBottom: '-10px', marginBottom: '-10px' }}
            >
              {scaleGroup.agendas.map((agendaItem) => (
                <li
                  key={agendaItem.id}
                  className="mb-3 p-2 border-b border-gray-200 last:border-b-0"
                >
                  <div className="flex items-center mb-1">
                    <div>
                      <Avatar
                        alt="User image"
                        src={agendaItem.user?.image_url}
                        width={10}
                        height={10}
                      />
                    </div>
                    <div style={{ marginLeft: '10px' }}>
                      <h4 className="font-semibold">
                        {agendaItem.user?.name || 'Médico N/A'}
                      </h4>
                      <span className="text-xs text-gray-500">
                        CRM: {agendaItem.user?.crm?.number || 'N/A'}/
                        {agendaItem.user?.crm?.state?.name || 'N/A'}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="block">
                      Início:{' '}
                      {moment(agendaItem.date_start).format('DD/MM HH:mm')}
                    </span>
                    <span className="block" style={{ float: 'right' }}>
                      Fim: {moment(agendaItem.date_end).format('DD/MM HH:mm')}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">
              Nenhum médico agendado para esta escala hoje.
            </p>
          )}
        </div>
      );
    });
  };

  return (
    <div className="w-full mx-auto p-6 font-sans">
      <h1 className="text-4xl font-extrabold text-center bg-gradient-to-r from-blue-600 to-cyan-400 bg-clip-text text-transparent drop-shadow-md mb-4">
        Escala de Médicos - Dia Atual
      </h1>
      <div className="text-center text-xl text-gray-700 font-medium mb-6">
        🕒 {formatDateTime(currentTime)}
      </div>

      {loadingScales ? (
        <p className="text-center text-gray-500 mt-6">Carregando escalas...</p>
      ) : unitId && scalesFilter?.dataScalesFilter.length > 0 ? (
        <div className="relative">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 -mx-2 gap-4">
            {renderScalesWithDoctors()}
          </div>
        </div>
      ) : (
        <p className="text-center text-gray-500 mt-6">
          Nenhuma escala encontrada para a unidade selecionada.
        </p>
      )}
    </div>
  );
}
